
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { BoPanel, IOnOffHandlers } from 'bonomo';
import { ICommunity } from '@/model/Community/communityInterfaces';
import { INTEGRATION_COMMUNICATION_SLUGS, MODULE_SLUG_INTEGRATIONS } from '@/model/Community/communityConstants';
import { IAuthUserData } from '@/store/modules/authModule';
import { EPermissionActions } from '@/model/EPermissionActions';
import { PermissionHelper } from '@/helpers/PermissionHelper';
import CommunityNav from '@/components/community/widget/CommunityNav.vue';
import Datim from '@/components/shared/Datim.vue';
import SimpleConfirmationDialog from '@/components/shared/SimpleConfirmationDialog.vue';
const communityModule = namespace('communityModule');
const authModule = namespace('authModule');

@Component({
  name: 'Community',
  components: { BoPanel, CommunityNav, Datim, SimpleConfirmationDialog },
})
export default class Community extends Vue {
  @Prop()
  protected communityId!: number;

  @communityModule.Getter('community')
  protected community!: ICommunity;

  @communityModule.Action('fetchCommunity')
  private fetchCommunity!: (id: number) => Promise<any>;

  @communityModule.Action('deleteCommunity')
  private deleteCommunity!: (communityId: number) => Promise<any>;

  @communityModule.Action('restoreCommunity')
  private restoreCommunity!: (communityId: number) => Promise<any>;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  protected loading: boolean = true;
  protected deleteDialogVisible: boolean = false;
  protected restoreDialogVisible: boolean = false;
  private subscribes: IOnOffHandlers = {};

  get displayRentbackCredit(): string {
    return this.community && this.community.rentback_credit != null ? String(this.community.rentback_credit) : '—';
  }

  get displayRentbackCap(): string {
    return this.community && this.community.rentback_cap != null ? String(this.community.rentback_cap) : '—';
  }

  get displayVertical(): string {
    return this.community && this.community.vertical != null
      ? this.$t('lockers.locker.fields.vertical.values.' + this.community.vertical).toString()
      : '—';
  }

  get gmapUrl(): string {
    return this.community && this.community.coordinates_lat && this.community.coordinates_long
      ? 'https://maps.google.com/?q=' + this.community.coordinates_lat + ',' + this.community.coordinates_long
      : '';
  }

  get integrationTypeName(): string | null {
    if (!this.community || !this.community.modules || !this.community.submodules) return null;
    const integrationsModule = this.community.modules.find((mod) => mod.slug === MODULE_SLUG_INTEGRATIONS);
    const integrationTypes = integrationsModule
      ? this.community.submodules.filter((submodule) => submodule.module_id === integrationsModule.id
        && INTEGRATION_COMMUNICATION_SLUGS.indexOf(submodule.slug) === -1)
      : [];
    return integrationTypes.length ? integrationTypes.map((integrationType) => integrationType.name).join(' | ') : null;
  }

    get canEdit(): boolean {
    return this.$canAction(EPermissionActions.ACTION_COMMUNITIES_UPDATE);
  }

  get canAdmin(): boolean {
    return this.$canAction(EPermissionActions.ACTION_COMMUNITIES_ADMIN);
  }

  get isDeleted(): boolean {
    return !!(this.community && this.community.deleted_at);
  }

  get canManageSecurityCode(): boolean {
    return this.$canAction(EPermissionActions.ACTION_SECURITY_CODES_GENERATE)
      || PermissionHelper.hasRoleAdministrator(this.authUser);
  }

  get clientTypeOptions() {
    return [
      { text: this.$t('communities.fields.clientTypes.trial'), value: 'trial' },
      { text: this.$t('communities.fields.clientTypes.live'), value: 'live' },
    ];
  }

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    return iso.substring(0, 10);
  }

  statusColor(status: string): string {
    if (status === 'active') return 'green lighten-4';
    if (status === 'inactive') return 'red lighten-4';
    return 'grey lighten-3';
  }

  statusTextColor(status: string): string {
    if (status === 'active') return 'green darken-3';
    if (status === 'inactive') return 'red darken-3';
    return 'grey darken-2';
  }

  created() {
    this.subscribes = this.$ons({});
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  loadData() {
    this.fetchCommunity(this.communityId);
  }

  onConfirmDelete() {
    this.deleteCommunity(this.communityId).then(() => {
      this.deleteDialogVisible = false;
      this.$uiBus.emit('success', this.$t('communities.edit.softDelete'));
      this.$router.push({ name: 'communityList' });
    });
  }

  onConfirmRestore() {
    this.restoreCommunity(this.communityId).then(() => {
      this.restoreDialogVisible = false;
      this.$uiBus.emit('success', this.$t('communities.edit.restore'));
      this.fetchCommunity(this.communityId);
    });
  }

}
